'use client';

/**
 * StartFlowClient — top-level state machine for the /start funnel.
 *
 * Orchestrates the question + interstitial sequence, owns the profile
 * + lead-form state, and handles the API calls that persist progress
 * incrementally (/api/health-profile/upsert) + finalize at submit
 * (/api/health-profile/complete + /api/intake/submit + /checkout/plan).
 *
 * The pending-user better-auth session is established by /api/intake/start
 * before this component mounts (the page-level server component handles
 * that), so every API call here just inherits the session cookie.
 *
 * Hydration safety: server passes `initialQuestionIdx` via props
 * (resume support, eventually). We default to 0; same on SSR + client.
 * No Date.now / locale reads in render.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useRouter } from 'next/navigation';
import { useReducedMotion } from 'framer-motion';
import { ProgressBar, BackArrow, PrimaryButton, QuizHero, TrustBadgeRow, QuizThemeProvider } from './StartUI';
import {
  ConcernsStep, EnergyStep, FocusStep, MemoryStep, SleepBandStep,
  LifestyleStep, ConditionsStep,
  FamilyHistoryStep, GoalStep, EmailPhoneStep,
  DobSexZipStep, MenstrualStep,
  type LeadForm,
} from './Steps';
import { Interstitial } from './Interstitial';
import {
  buildFlow, totalQuestionCount, canAdvanceFrom,
  type FlowStep, type QuestionId,
} from '@/lib/health-profile/quiz-state';
import { selectInterstitial } from '@/lib/health-profile/interstitial-content';
import { QUIZ_QUESTION_THEME, QUIZ_INTERSTITIAL_THEME } from './quiz-theme';
import { LegalLink } from '@/components/LegalLink';
import type { LegalSlug } from '@/components/LegalModal';
import {
  EMPTY_SERIALIZED_QUIZ_OVERRIDES,
  type SerializedQuizOverrides,
} from '@/lib/quiz/types';
import { labelMissingFields } from '@/lib/health-profile/labels';
import {
  SIGNUP_CHECKBOX_LABEL,
  SIGNUP_CHECKBOX_LINKS,
} from '@/lib/consent/copy';
import type {
  HealthProfileInput,
  PrimaryConcern,
  ActivityLevel,
  DietPattern,
  EnergyPattern,
  CognitiveState,
  MemoryState,
  SleepHoursBand,
  DiagnosedCondition,
  FamilyHistoryCategory,
  PrimaryGoal,
  MenstrualStatus,
  CyclePhase,
  BirthControlType,
  BiometricsUnitPref,
} from '@/lib/health-profile/types';

const EMPTY_LEAD: LeadForm = {
  firstName: '', lastName: '', email: '',
  phone: '', smsConsent: false,
  dateOfBirth: '', sexAtBirth: '', zipCode: '',
};

/** Profile fields that get persisted to the API per question step. */
const FIELDS_PER_STEP: Record<QuestionId, (keyof HealthProfileInput)[]> = {
  concerns:       ['primaryConcerns'],
  // Phase 1: 4 new dimension steps; lifestyle slimmed to activity + diet only;
  // habits step (smoking + alcohol) removed entirely.
  energy:         ['energyPattern'],
  focus:          ['cognitiveState'],
  memory:         ['memoryState'],
  sleep_band:     ['sleepHoursBand'],
  lifestyle:      ['activityLevel', 'dietPattern'],
  conditions:     ['diagnosedConditions'],
  family_history: ['familyHistory'],
  goal:           ['primaryGoals'],
  email_phone:    [],  // lead form (firstName + lastName + email) — submitted at final step
  dob_sex_zip:    [],
  menstrual:      ['menstrualStatus', 'cyclePhase', 'birthControlType'],
};

// Phase 1: no slider default needed — sleep is now a card-pick step.
// canAdvanceFrom('sleep_band') gates on sleepHoursBand being set explicitly.
const INITIAL_PROFILE: HealthProfileInput = {};

// Track 5.Y6-auto-nav-single-select (2026-05-12): step IDs whose single
// answer auto-advances 250ms after selection. Multi-select / composite /
// form steps are intentionally absent — concerns + conditions + family_
// history are multi-select; lifestyle has activity + diet on one screen;
// goal IS multi-select (primary_goals: PrimaryGoal[]); name / email_phone
// / dob_sex_zip are form fields; menstrual is composite. Buyer's intent
// on these 4 single-select dimension steps is unambiguous — one click =
// answer — so the manual Continue is unnecessary friction.
const AUTO_ADVANCE_FIELDS: Partial<Record<QuestionId, keyof HealthProfileInput>> = {
  energy:     'energyPattern',
  focus:      'cognitiveState',
  memory:     'memoryState',
  sleep_band: 'sleepHoursBand',
};
const AUTO_ADVANCE_DELAY_MS = 250;
const AUTO_ADVANCE_DELAY_REDUCED_MS = 100;

export function StartFlowClient({
  overrides = EMPTY_SERIALIZED_QUIZ_OVERRIDES,
}: {
  overrides?: SerializedQuizOverrides;
} = {}) {
  const router = useRouter();
  const [profile, setProfile] = useState<HealthProfileInput>(INITIAL_PROFILE);
  const [lead, setLead] = useState<LeadForm>(EMPTY_LEAD);
  const [stepIdx, setStepIdx] = useState(0);
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Bootstrap: ensure the pending-user session exists before the
  // first /api/health-profile/upsert call. /api/intake/start is
  // idempotent — if a session already exists, the route just returns
  // ok. Fires once on mount; failure is silent (the first profile
  // save will surface a setSubmitError if it 401s).
  useEffect(() => {
    fetch('/api/intake/start', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      credentials: 'include',
      body: '{}',
    }).catch(() => { /* surfaced by saveCurrentQuestion if it matters */ });
  }, []);

  const flow = useMemo<FlowStep[]>(
    // Track 3.2 (2026-05-13): flow length is sex-INDEPENDENT for visual
    // stability — we always render the female-shaped flow (includes the
    // menstrual step) so picking Male at dob_sex_zip doesn't dynamically
    // shrink the progress bar or flip the CTA label from "Continue →" to
    // "Continue to plan →". Sex-aware routing (male skips menstrual to
    // /checkout/plan) lives in next() at the dob_sex_zip branch below,
    // gated on lead.sexAtBirth, NOT on flow.length.
    () => buildFlow({ sexAtBirth: 'female' }),
    [],
  );
  const totalQs = useMemo(() => totalQuestionCount(flow), [flow]);
  const step = flow[Math.min(stepIdx, flow.length - 1)];

  const updateProfile = useCallback((patch: Partial<HealthProfileInput>) => {
    setProfile((p) => ({ ...p, ...patch }));
  }, []);
  const updateLead = useCallback((patch: Partial<LeadForm>) => {
    setLead((l) => ({ ...l, ...patch }));
  }, []);

  /** Persist the current question's fields to /api/health-profile/upsert. */
  async function saveCurrentQuestion(): Promise<boolean> {
    if (step.kind !== 'question') return true;
    const fields = FIELDS_PER_STEP[step.id];
    if (fields.length === 0) return true; // lead-form steps; no API call here
    const body: Partial<HealthProfileInput> = {};
    for (const k of fields) {
      const v = (profile as Record<string, unknown>)[k as string];
      if (v !== undefined) (body as Record<string, unknown>)[k as string] = v;
    }
    if (Object.keys(body).length === 0) return true;
    const res = await fetch('/api/health-profile/upsert', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(body),
    });
    return res.ok;
  }

  async function next() {
    if (busy) return;
    setBusy(true);
    setSubmitError(null);
    try {
      const ok = await saveCurrentQuestion();
      if (!ok) {
        setSubmitError('Could not save — please retry.');
        return;
      }
      // Track 5.Y5-lead-fires-at-step-12 (2026-05-12): intake/submit
      // fires at the END of dob_sex_zip (step 12), not at the final
      // step. Lead-event capture happens earlier in the flow with full
      // PII (email + name + dob + sex + zip + phone). Consent has
      // already been collected at step 11 (email_phone) so the
      // submitIntake call carries the legal-basis bundle. For male
      // users, dob_sex_zip IS the last step — submit + redirect to
      // /checkout/plan immediately. For female users, advance to step
      // 13 (menstrual) which becomes a post-lead enrichment step;
      // submit does NOT re-fire there.
      if (step.kind === 'question' && step.id === 'dob_sex_zip') {
        const submitOk = await submitIntake();
        if (!submitOk) return;  // submitIntake sets submitError (or already navigated)
        // Track 3.2 (2026-05-13): male users skip the menstrual step and
        // land on /checkout/plan directly. Decision is sex-driven, not
        // flow-length-driven, so the visible flow length can stay 13
        // for both sexes (visual stability on gender selection).
        if (lead.sexAtBirth === 'male') {
          router.push('/checkout/plan');
          return;
        }
        setStepIdx((i) => i + 1);
        return;
      }
      // Final step (menstrual for female users, post-submit) — user
      // is already a lead, just finish the profile + redirect.
      if (stepIdx >= flow.length - 1) {
        router.push('/checkout/plan');
        return;
      }
      setStepIdx((i) => i + 1);
    } finally {
      setBusy(false);
    }
  }

  // Track 5.Y6-auto-nav-single-select (2026-05-12): auto-advance on
  // single-select dimension steps. Watches the per-step profile field;
  // schedules next() ~250ms after the value transitions from the
  // step-entry baseline. useEffect cleanup naturally handles "user
  // picks card A, then picks card B before timer fires" — the first
  // timer is cleared on re-entry. `stepEntryBaselineRef` snapshots
  // the field value when the user lands on the step, so navigating
  // BACK to a pre-answered step does NOT trigger auto-advance (avoids
  // a Back-ping-pong loop). `nextRef` indirection ensures the timer's
  // callback uses the LATEST next() (which closures over the latest
  // profile state), not a stale closure from when the timer was
  // scheduled.
  const reduce = useReducedMotion();
  const nextRef = useRef(next);
  nextRef.current = next;
  const stepEntryBaselineRef = useRef<{ stepId: QuestionId | null; value: unknown }>({ stepId: null, value: undefined });

  useEffect(() => {
    if (step.kind !== 'question') {
      stepEntryBaselineRef.current = { stepId: null, value: undefined };
      return;
    }
    const fieldName = AUTO_ADVANCE_FIELDS[step.id];
    if (!fieldName) {
      stepEntryBaselineRef.current = { stepId: null, value: undefined };
      return;
    }
    const value = (profile as Record<string, unknown>)[fieldName];

    // First entry to this step (or re-entry via Back): snapshot the
    // current value as the baseline. Do NOT auto-advance — even if
    // the value is already set (Back-navigation case).
    if (stepEntryBaselineRef.current.stepId !== step.id) {
      stepEntryBaselineRef.current = { stepId: step.id, value };
      return;
    }

    // Same step, value transitioned (user clicked a card on the
    // already-active step). Auto-advance if the new value differs
    // from the baseline. Same-value re-clicks → no-op (React skips
    // the render → useEffect doesn't even fire).
    if (value && value !== stepEntryBaselineRef.current.value) {
      const delay = reduce ? AUTO_ADVANCE_DELAY_REDUCED_MS : AUTO_ADVANCE_DELAY_MS;
      const timer = setTimeout(() => nextRef.current(), delay);
      return () => clearTimeout(timer);
    }
    // Depending on the 4 specific single-select fields (not the whole
    // `profile` object) is intentional — the effect should re-run only
    // when an auto-nav-eligible field changes. Adding `profile` would
    // schedule timers on every multi-select / form-field state change
    // too, then immediately bail out at the `fieldName` lookup.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, profile.energyPattern, profile.cognitiveState, profile.memoryState, profile.sleepHoursBand, reduce]);

  function back() {
    if (busy) return;
    if (stepIdx === 0) return;
    setStepIdx((i) => Math.max(0, i - 1));
  }

  /**
   * Track 5.Y5: returns true on success (advance / redirect to follow),
   * false on a handled failure (setSubmitError already set, or
   * already_promoted path which router.push'd to /checkout/plan).
   */
  async function submitIntake(): Promise<boolean> {
    if (!consent) {
      // Defensive — canAdvanceFromStep('email_phone') gates on consent
      // so this branch shouldn't be reachable from the UI today.
      setSubmitError('Please accept the consent to continue.');
      return false;
    }
    // 1. Final profile complete check
    const completeRes = await fetch('/api/health-profile/complete', {
      method: 'POST', credentials: 'include',
    });
    if (!completeRes.ok) {
      const body = (await completeRes.json().catch(() => ({}))) as { missingFields?: string[] };
      const missing = body.missingFields ?? [];
      setSubmitError(
        missing.length > 0
          ? `Some answers got dropped. Could you check: ${labelMissingFields(missing)}?`
          : 'Could not finish — please try submitting again.',
      );
      return false;
    }
    // 2. Submit intake (consent + payload) — fires the /api/intake/submit
    //    route. Lead-form data lives in the payload jsonb. Track 5.Y5
    //    moves this fire from the final step to the end of dob_sex_zip
    //    so the Lead event captures users who get all the way through
    //    PII collection (email + name + dob + sex + zip + phone) but
    //    bail on the female-only menstrual step. Consent was collected
    //    at step 11 (email_phone).
    const intakeRes = await fetch('/api/intake/submit', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({
        payload: {
          firstName: lead.firstName, lastName: lead.lastName,
          email: lead.email,
          phone: lead.phone, smsConsent: lead.smsConsent,
          dateOfBirth: lead.dateOfBirth, sexAtBirth: lead.sexAtBirth,
          zipCode: lead.zipCode,
        },
        consent: { accepted: true },
      }),
    });
    if (!intakeRes.ok) {
      const body = (await intakeRes.json().catch(() => ({}))) as { reason?: string; signinUrl?: string };
      switch (body.reason) {
        case 'email_taken': {
          const url = body.signinUrl ?? `/login?email=${encodeURIComponent(lead.email)}`;
          setSubmitError(
            `EMAIL_TAKEN::Looks like you've started this before. <a href="${url}">Sign in to pick up where you left off</a>, or use a different email.`,
          );
          return false;
        }
        case 'already_promoted': {
          router.push('/checkout/plan');
          return false;
        }
        case 'invalid_phone':
          setSubmitError('Please enter a valid 10-digit US phone number.');
          return false;
        case 'invalid_email':
          setSubmitError('Please enter a valid email address.');
          return false;
        case 'invalid_name':
          setSubmitError('Please enter your first and last name.');
          return false;
        case 'placeholder_email':
          setSubmitError('That email looks like a test placeholder — please use a real address.');
          return false;
        case 'no_session':
          setSubmitError('Your session expired. Refresh the page and try again.');
          return false;
        case 'no_user':
          setSubmitError('We could not find your account. Refresh the page and start over.');
          return false;
        case 'consent_required':
          setSubmitError('Please accept the consent to continue.');
          return false;
        case 'payload_too_large':
          setSubmitError('Your submission is too large. Try shortening any free-text answers.');
          return false;
        case 'db_write_failed':
        default:
          setSubmitError('Something went wrong on our end. Try again, or reach support@feelgood.health if it keeps happening.');
          return false;
      }
    }
    return true;
  }

  // ── Render ──────────────────────────────────────────────────────────

  // Phase 2 (Lock 5a): theme + icon for the active step. Interstitials
  // inherit the theme of the *next* question they sit before; if the
  // last interstitial has no following question (shouldn't happen in
  // current flow), fall back to the slot's default theme.
  const theme = step.kind === 'question'
    ? QUIZ_QUESTION_THEME[step.id]
    : QUIZ_INTERSTITIAL_THEME[step.slot];

  // Concerns (step 1) gets trust badges (Lock 5g); other question
  // steps don't. Interstitials carry their own credibility chrome.
  const showTrustBadges = step.kind === 'question' && step.id === 'concerns';

  // Step transition fade key — increments on every step change so
  // React re-runs the fade-in keyframe (Lock 5e).
  const stepFadeKey = step.kind === 'question' ? `q:${step.id}` : `i:${step.afterQuestion}:${step.slot}`;

  // Track 3.13-phase-0: container width is step-aware. Question steps
  // stay narrow (max-w-lg ≈ 512px) so option-card lists scan quickly;
  // interstitials get max-w-2xl (≈ 672px) so future value/proof/upgraded-
  // education slides have room for comparison graphics, testimonial faces,
  // and animated CSS data viz without breaking the centered-card register.
  // BackArrow chrome + step counter expand with the container so the
  // visual frame stays coherent within each interstitial.
  const containerWidth = step.kind === 'interstitial' ? 'max-w-2xl' : 'max-w-lg';

  return (
    <main className="min-h-screen bg-white">
      <div className={`${containerWidth} mx-auto px-6 py-8`}>
        <div className="flex items-center justify-between mb-6">
          {stepIdx > 0 ? (
            <BackArrow onClick={back} />
          ) : (
            <span className="text-sm font-bold tracking-tight text-[#0f2534]">feelgood.health</span>
          )}
          <span className="text-xs text-slate-500">
            Step {step.displayNumber} of {totalQs}
          </span>
        </div>

        <div className="mb-6">
          <ProgressBar current={step.displayNumber} total={totalQs} themeColor={theme.themeColor} />
        </div>

        {/* Hero band (Lock 5a) sits above the step content. Renders
            for question steps EXCEPT concerns (Track 5.Z3 step-1
            hierarchy fix — top-right "Step N of M" caption +
            progress-dot bar already carry step-numbering metadata,
            and the H1 should be the visual anchor on the entry
            screen). Interstitials carry their own full-section visual
            treatment instead. */}
        {step.kind === 'question' && step.id !== 'concerns' && (
          <div className="mb-6">
            <QuizHero
              themeColor={theme.themeColor}
              Icon={theme.Icon}
              eyebrow={`Step ${step.displayNumber} of ${totalQs}`}
            />
          </div>
        )}

        {/* Step transition fade wrapper (Lock 5e) — keyed on step id so
            the keyframe re-runs on every advance/back. ThemeProvider
            wraps the active step's content so descendant OptionCards
            pick up the per-step accent without prop drilling. */}
        <QuizThemeProvider themeColor={theme.themeColor}>
          <div key={stepFadeKey} className="animate-quiz-step-in space-y-8">
            {step.kind === 'interstitial' ? (
              <Interstitial
                slide={selectInterstitial(step.slot, profile)}
                onContinue={next}
                anchorQuestionId={step.afterQuestion}
                displayNumber={step.displayNumber}
                themeColor={theme.themeColor}
              />
            ) : (
              <>
                {renderQuestion(step.id, profile, lead, updateProfile, updateLead, overrides)}

                {showTrustBadges && <TrustBadgeRow />}

                {/* Track 5.Y5 (2026-05-12): consent block moved from the
                    LAST step to step 11 (email_phone). Legal basis is
                    on record before /api/intake/submit fires at step 12
                    (which now happens mid-flow, not at the end). */}
                {step.kind === 'question' && step.id === 'email_phone' && (
                  <ConsentBlock
                    consent={consent}
                    onChange={setConsent}
                  />
                )}

                {submitError && <SubmitErrorMessage error={submitError} />}

                {/* Track 5.Y6 (2026-05-12): Continue is hidden on single-
                    select dimension steps that auto-advance — the
                    selected-state checkmark + 250ms delay + screen
                    transition is sufficient feedback. Continue RE-APPEARS
                    when a submit error is set (e.g. /api/health-profile/
                    upsert failure) so the user has an escape hatch to
                    manually retry. */}
                {(!(step.kind === 'question' && AUTO_ADVANCE_FIELDS[step.id]) || submitError) && (
                  <PrimaryButton
                    label={stepIdx >= flow.length - 1 ? 'Continue to plan →' : 'Continue →'}
                    onClick={next}
                    disabled={!canAdvanceFromStep(step.id, profile, lead, consent, stepIdx, flow.length)}
                    loading={busy}
                  />
                )}
              </>
            )}
          </div>
        </QuizThemeProvider>
      </div>
    </main>
  );
}

function renderQuestion(
  id: QuestionId,
  profile: HealthProfileInput,
  lead: LeadForm,
  updateProfile: (p: Partial<HealthProfileInput>) => void,
  updateLead: (p: Partial<LeadForm>) => void,
  overrides: SerializedQuizOverrides,
) {
  switch (id) {
    case 'concerns':       return <ConcernsStep profile={profile} onChange={updateProfile} overrides={overrides} />;
    case 'energy':         return <EnergyStep profile={profile} onChange={updateProfile} overrides={overrides} />;
    case 'focus':          return <FocusStep profile={profile} onChange={updateProfile} overrides={overrides} />;
    case 'memory':         return <MemoryStep profile={profile} onChange={updateProfile} overrides={overrides} />;
    case 'sleep_band':     return <SleepBandStep profile={profile} onChange={updateProfile} overrides={overrides} />;
    case 'lifestyle':      return <LifestyleStep profile={profile} onChange={updateProfile} overrides={overrides} />;
    case 'conditions':     return <ConditionsStep profile={profile} onChange={updateProfile} overrides={overrides} />;
    case 'family_history': return <FamilyHistoryStep profile={profile} onChange={updateProfile} overrides={overrides} />;
    case 'goal':           return <GoalStep profile={profile} onChange={updateProfile} overrides={overrides} />;
    // Track 3.10 (2026-05-14): 'name' merged into 'email_phone'. NameStep
    // import is no longer needed; if you're reviving the separate step,
    // restore from git history and re-add the QuestionId entry.
    case 'email_phone':    return <EmailPhoneStep lead={lead} onLeadChange={updateLead} overrides={overrides} />;
    case 'dob_sex_zip':    return <DobSexZipStep lead={lead} onLeadChange={updateLead} overrides={overrides} />;
    case 'menstrual':      return <MenstrualStep profile={profile} onChange={updateProfile} overrides={overrides} />;
  }
}

function canAdvanceFromStep(
  id: QuestionId,
  profile: HealthProfileInput,
  lead: LeadForm,
  consent: boolean,
  stepIdx: number,
  flowLen: number,
): boolean {
  // Lead-form steps gate on lead-state, not profile-state.
  // Track 3.10 (2026-05-14): email_phone now captures firstName + lastName
  // in addition to email + consent (merged former 'name' step). Gate
  // requires all four. Track 5.Y5's email_phone-as-Lead-fire callsite
  // (consent gating) preserved — Lead event still fires at this step.
  if (id === 'email_phone') return Boolean(
    lead.firstName.trim() &&
    lead.lastName.trim() &&
    lead.email.trim() &&
    /.+@.+\..+/.test(lead.email) &&
    consent,
  );
  if (id === 'dob_sex_zip') {
    // Phone required (Track 5.Y2 — §6.2 2026-05-12). 10-digit US.
    // SMS opt-in checkbox is OPTIONAL (TCPA — explicit consent required
    // to text, but the field itself is required).
    const phoneOk = lead.phone.replace(/\D/g, '').length === 10;
    return Boolean(lead.dateOfBirth && lead.sexAtBirth && lead.zipCode.trim() && phoneOk);
  }
  // Track 5.Y5: removed the previous `stepIdx >= flowLen - 1 && !consent`
  // gate — consent is now gated at step 11 (email_phone) above, so by
  // the time the user reaches any later step it's guaranteed checked.
  // _stepIdx + _flowLen kept in the signature for caller compat.
  void stepIdx;
  void flowLen;
  return canAdvanceFrom(id, profile);
}

/**
 * Track 5.Y2: submit-error renderer that distinguishes plain-text errors
 * from the email_taken case (which embeds an inline /login link).
 *
 * The email_taken format is `EMAIL_TAKEN::<message-with-anchor-tag>`.
 * The anchor href is constructed server-side or client-side; both
 * sources funnel through this renderer so the inline link is real
 * navigation, not a literal `<a>` string in the page. Other errors
 * render as plain text — no innerHTML escape hatch for arbitrary content.
 */
function SubmitErrorMessage({ error }: { error: string }) {
  if (error.startsWith('EMAIL_TAKEN::')) {
    const html = error.slice('EMAIL_TAKEN::'.length);
    return (
      <p
        className="text-sm text-red-600 [&_a]:underline [&_a]:font-semibold [&_a]:text-red-700 [&_a:hover]:text-red-800"
        dangerouslySetInnerHTML={{ __html: html }}
      />
    );
  }
  return <p className="text-sm text-red-600">{error}</p>;
}

function ConsentBlock({ consent, onChange }: { consent: boolean; onChange: (v: boolean) => void }) {
  return (
    <label className="flex items-start gap-3 cursor-pointer pt-2">
      <input
        type="checkbox"
        checked={consent}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-1 h-4 w-4 rounded border-slate-300 text-amber-500 focus:ring-amber-500"
      />
      <span className="text-xs text-slate-600 leading-relaxed">
        {SIGNUP_CHECKBOX_LABEL}
        <span className="block mt-1 text-slate-400">
          {SIGNUP_CHECKBOX_LINKS.map((l, i) => {
            // Track 3.3 (2026-05-13): legal links open in-page modal so a
            // mid-quiz misclick doesn't drop the buyer off /start. The
            // 3 SIGNUP_CHECKBOX_LINKS hrefs all match LegalSlug keys
            // 1:1 by stripping the leading slash.
            const slug = l.href.slice(1) as LegalSlug;
            return (
              <span key={l.href}>
                <LegalLink slug={slug} className="underline hover:text-[#0f2534]">{l.label}</LegalLink>
                {i < SIGNUP_CHECKBOX_LINKS.length - 1 && ' · '}
              </span>
            );
          })}
        </span>
      </span>
    </label>
  );
}

// Suppress unused-warning rumblings — the enum imports are used by
// types referenced in Steps.tsx but TS sometimes still flags them
// when re-exported across boundaries.
void [
  null as unknown as PrimaryConcern, null as unknown as ActivityLevel,
  null as unknown as DietPattern, null as unknown as EnergyPattern,
  null as unknown as CognitiveState, null as unknown as MemoryState,
  null as unknown as SleepHoursBand, null as unknown as DiagnosedCondition,
  null as unknown as FamilyHistoryCategory, null as unknown as PrimaryGoal,
  null as unknown as MenstrualStatus, null as unknown as CyclePhase,
  null as unknown as BirthControlType, null as unknown as BiometricsUnitPref,
];
